import React from "react";
import {
  Box,
  Typography,
  TextField,
  FormGroup,
  Checkbox,
  FormControlLabel,
  Tooltip,
  IconButton,
  Button,
} from "@mui/material";
import PropTypes from "prop-types";
import SystemSizeCard from "./SystemSizeCard";
import { DataGridPro as DataGrid } from "@mui/x-data-grid-pro";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import PlaylistPlayOutlined from "@mui/icons-material/PlaylistPlayOutlined";

const dataGridBaseSx = {
  "&, .MuiDataGrid-columnHeader": {
    borderTop: "none",
  },
  "&, [class^=MuiDataGrid]": {
    overflowX: "hidden",
    borderLeft: "none",
    borderRight: "none",
  },
  "& .MuiDataGrid-row.Mui-selected": {
    backgroundColor: "rgba(0, 0, 0, 0.04) !important",
  },
  "& .MuiDataGrid-columnSeparator": {
    display: "none",
  },
  "& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--sorted) .MuiDataGrid-iconButtonContainer":
    {
      display: "none",
    },
};

const dataGridSetupSx = {
  ...dataGridBaseSx,
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "transparent",
  },
};

const SiteAssets = (props) => {
  const {
    styles,
    theme,
    currentSite,
    getPowerOutput,
    getEnergyOutput,
    assets,
    tables,
    flags,
    handlers,
    lists,
    assetsTypeList,
  } = props;

  const assetValues = assets || {};
  const tableData = tables || {};
  const loadFlags = flags || {};
  const actionHandlers = handlers || {};

  return (
    <>
      <Box display="flex" flexDirection="column" sx={styles.settingsPanel} marginTop="1rem">
        <FormGroup sx={{ width: "11.5rem", marginRight: "1.5rem" }}>
          <Box mb="-4rem">
            <SystemSizeCard
              sitePowerCapacity={getPowerOutput(currentSite)}
              siteEnergyCapacity={getEnergyOutput(currentSite)}
              solarOutput={assetValues.solarCapacity}
              batteryOutput={assetValues.batteryCapacity}
              gensetOutput={assetValues.gensetCapacity}
              batteryStorage={assetValues.batteryEnergyCapacity}
            />
          </Box>
          <Typography variant="h5" mb="1.5rem">
            Size Inputs
          </Typography>
          <Box display="flex" mb="1.5rem" alignItems="center">
            <TextField
              id="solar"
              name="solar"
              size="small"
              label="Solar (kW)"
              value={assetValues.solarCapacity}
              variant="outlined"
              onChange={(ev) => {
                assets?.setSolarCapacity ? assets.setSolarCapacity(ev.target.value) : null;
              }}
              sx={{
                width: "11.5rem",
                fontSize: 16,
                fontWeight: 400,
                marginRight: "1.5rem",
              }}
              error={
                (isNaN(assetValues.solarCapacity) || assetValues.solarCapacity < 0) &&
                assetValues.solarCapacity?.length > 0
              }
              helperText={
                (isNaN(assetValues.solarCapacity) || assetValues.solarCapacity < 0) &&
                assetValues.solarCapacity.length > 0
                  ? "Capacity must be a number greater than 0"
                  : ""
              }
            />
            <TextField
              id="battery"
              name="battery"
              size="small"
              label="Battery Power (kW)"
              value={assetValues.batteryCapacity}
              variant="outlined"
              onChange={(ev) => {
                assets?.setBatteryCapacity ? assets.setBatteryCapacity(ev.target.value) : null;
              }}
              sx={{
                width: "11.5rem",
                fontSize: 16,
                fontWeight: 400,
                marginRight: "1.5rem",
              }}
              error={
                (isNaN(assetValues.batteryCapacity) || assetValues.batteryCapacity < 0) &&
                assetValues.batteryCapacity?.length > 0
              }
              helperText={
                (isNaN(assetValues.batteryCapacity) || assetValues.batteryCapacity < 0) &&
                assetValues.batteryCapacity?.length > 0
                  ? "Battery power capacity must be a number greater than 0"
                  : ""
              }
            />
            <TextField
              id="batteryEnergy"
              name="batteryEnergy"
              size="small"
              label="Battery Energy (kWh)"
              value={assetValues.batteryEnergyCapacity}
              variant="outlined"
              onChange={(ev) => {
                assets?.setBatteryEnergyCapacity
                  ? assets.setBatteryEnergyCapacity(ev.target.value)
                  : null;
              }}
              sx={{
                width: "11.5rem",
                fontSize: 16,
                fontWeight: 400,
                marginRight: "1.5rem",
              }}
              error={
                (isNaN(assetValues.batteryEnergyCapacity) ||
                  assetValues.batteryEnergyCapacity < 0) &&
                assetValues.batteryEnergyCapacity?.length > 0
              }
              helperText={
                (isNaN(assetValues.batteryEnergyCapacity) ||
                  assetValues.batteryEnergyCapacity < 0) &&
                assetValues.batteryEnergyCapacity.length > 0
                  ? "Battery energy must be a number greater than 0"
                  : ""
              }
            />
            <TextField
              id="genset"
              name="genset"
              size="small"
              label="Genset (kW)"
              value={assetValues.gensetCapacity}
              variant="outlined"
              onChange={(ev) => {
                assets?.setGensetCapacity ? assets.setGensetCapacity(ev.target.value) : null;
              }}
              sx={{
                width: "11.5rem",
                fontSize: 16,
                fontWeight: 400,
                marginRight: "1.5rem",
              }}
              error={
                (isNaN(assetValues.gensetCapacity) || assetValues.gensetCapacity < 0) &&
                assetValues.gensetCapacity?.length > 0
              }
              helperText={
                (isNaN(assetValues.gensetCapacity) || assetValues.gensetCapacity < 0) &&
                assetValues.gensetCapacity?.length > 0
                  ? "Capacity must be a number greater than 0"
                  : ""
              }
            />
          </Box>
        </FormGroup>
      </Box>

      <Box display="flex" flexDirection="column" sx={styles.settingsPanel} marginTop="-2.5rem">
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h5">Connected Assets</Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextField
              id="edge_id"
              name="edge_id"
              label="Edge Group ID"
              variant="outlined"
              size="small"
              value={assetValues.edgeId}
              onChange={(ev) => {
                assets?.setEdgeId ? assets.setEdgeId(ev.target.value) : null;
              }}
              sx={{
                width: "14rem",
                fontSize: 16,
                fontWeight: 400,
                marginRight: "1.5rem",
              }}
              InputProps={{
                sx: { fontSize: 16, fontWeight: 400 },
                endAdornment: (
                  <Tooltip
                    title={
                      <>
                        This input links this site to Scale Edge, to bring in the correct asset
                        data.
                        <br />
                        <br />
                        (Only relevant for Edge sites.)
                      </>
                    }
                  >
                    <IconButton size="small">
                      <LinkOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                ),
              }}
            />
          </Box>
        </Box>

        <Box mt={1}>
          <FormControlLabel
            control={
              <Checkbox
                size="large"
                checked={!!assetValues.hasLoad}
                onChange={(ev, checked) => {
                  assets?.setHasLoad ? assets.setHasLoad(checked) : null;
                }}
                disabled={!currentSite?.uuid}
              />
            }
            label="Show Load in Site Overview"
            sx={{
              "& .MuiFormControlLabel-label": {
                fontSize: "16px",
                color: "rgba(0, 0, 0, 0.87)",
              },
            }}
          />
        </Box>

        {loadFlags.siteSpec4 && (
          <Box>
            {loadFlags.isAssembliesLoading || loadFlags.manufacturersListIsLoading ? null : (
              <Box>
                {tableData.setupRows.length > 0 && (
                  <Box
                    sx={{
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      padding: "1.5rem",
                      backgroundColor: "#F9F9F9",
                      mt: "2rem",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: "1.5rem",
                      }}
                    >
                      <Typography variant="h6">Ready for Setup</Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PlaylistPlayOutlined />}
                        sx={{ textTransform: "none" }}
                        onClick={actionHandlers.onOpenPreFill}
                      >
                        Start Assets Setup
                      </Button>
                    </Box>
                    <div style={theme.classes.tableContainer}>
                      {loadFlags.isSetupDataReady && (tables?.assembliesList || true) ? (
                        tableData.setupRows.length > 0 ? (
                          <DataGrid
                            rows={tableData.setupRows}
                            columns={tableData.columns}
                            autoHeight
                            autoWidth
                            disableSelectionOnClick
                            disableColumnMenu
                            sortingOrder={["asc", "desc"]}
                            hideFooter
                            onRowClick={actionHandlers.handleRowClick}
                            sx={dataGridSetupSx}
                          />
                        ) : null
                      ) : null}
                    </div>
                  </Box>
                )}
                <div style={{ ...theme.classes.tableContainer }}>
                  {loadFlags.isDataReady && tableData.filteredRows
                    ? assetsTypeList.map((type) => {
                        const rows = tableData.filteredRows[type];
                        if (!rows || rows.length === 0) return null;
                        return (
                          <div
                            key={type}
                            style={{
                              ...theme.classes.tableContainer,
                              border: "1px solid #E1E2E4",
                              borderRadius: "4px",
                              marginTop: "2rem",
                              marginBottom: "2rem",
                              padding: "1.5rem",
                            }}
                          >
                            <Typography variant="h6" mt="1.5rem" mb="1rem">
                              {type}
                            </Typography>
                            <div style={theme.classes.tableContainer}>
                              <DataGrid
                                rows={rows}
                                columns={
                                  type === "Utility"
                                    ? tableData.columnsUtilityAssets
                                    : tableData.columnsReadyAssets
                                }
                                autoHeight
                                autoWidth
                                disableSelectionOnClick
                                disableColumnMenu
                                sortingOrder={["asc", "desc"]}
                                hideFooter
                                onRowClick={actionHandlers.handleRowClick}
                                sx={dataGridBaseSx}
                              />
                            </div>
                          </div>
                        );
                      })
                    : null}
                </div>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

SiteAssets.propTypes = {
  styles: PropTypes.object,
  theme: PropTypes.object,
  currentSite: PropTypes.object,
  getPowerOutput: PropTypes.func,
  getEnergyOutput: PropTypes.func,
  assets: PropTypes.object,
  tables: PropTypes.object,
  flags: PropTypes.object,
  handlers: PropTypes.object,
  lists: PropTypes.object,
  assetsTypeList: PropTypes.array,
};

export default SiteAssets;
